import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppLogo } from '@/assets/logos/app-logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLoginStepMotionTokens } from '../hooks/use-login-step-motion-tokens';
import { cn } from '@/utils/cn';
import { sendWebsiteLoginEmail, verifyWebsiteLoginToken } from '../lib/auth';
import {
  applyOnboardingInviteCode,
  getOnboardingState,
  resolveOnboardingPathForState,
  resolveWebOnboardingDownloadPath,
  writeOnboardingCompleteCache,
} from '../lib/onboarding';
import { useWebsiteTitlebar } from '../lib/titlebar';
import {
  isMacOSOnboardingRuntime,
  syncMacOSOnboardingSessionSoon,
  useMacOSOnboardingWindowRuntime,
} from '../components/onboarding/onboarding-native-runtime';

type WaitlistLoginParams = {
  intent: 'waitlist' | null;
  email: string;
  autoLogin: boolean;
};

function normalizeNext(next: string | null): string {
  if (!next || !next.startsWith('/')) {
    return '/chat';
  }
  return next;
}

function isValidLoginEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function parseWaitlistLoginParams(
  searchParams: URLSearchParams
): WaitlistLoginParams {
  if (searchParams.get('intent') !== 'waitlist') {
    return {
      intent: null,
      email: '',
      autoLogin: false,
    };
  }

  return {
    intent: 'waitlist',
    email: (searchParams.get('email') ?? '').trim(),
    autoLogin: searchParams.get('autoLogin') === '1',
  };
}

function initialInviteCodeFromSearchParams(searchParams: URLSearchParams) {
  return (
    searchParams.get('invite_code') ??
    searchParams.get('inviteCode') ??
    ''
  ).trim();
}

function buildConsumedAutoLoginPath(searchParams: URLSearchParams) {
  const nextParams = new URLSearchParams(searchParams);
  nextParams.delete('autoLogin');
  const query = nextParams.toString();
  return query ? `/login?${query}` : '/login';
}

const primaryButtonClass =
  'h-11 w-full rounded-[1rem] border-0 text-sm font-medium shadow-none transition-colors focus-visible:ring-0';

const secondaryButtonClass =
  'h-11 w-full rounded-[1rem] border-0 bg-transparent text-sm font-normal shadow-none hover:bg-transparent hover:text-foreground active:bg-transparent focus-visible:bg-transparent';

const inputClass =
  'h-11 rounded-xl border-border/80 bg-background px-3.5 text-sm transition-colors placeholder:text-[1em] focus-visible:border-border/80 focus-visible:outline-none focus-visible:ring-0';

const loginFormClass = 'w-full space-y-5 px-2';

const loginFieldErrorClass =
  'block w-full text-left text-(length:--login-caption-font-size) leading-snug text-destructive';

export function LoginPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const next = useMemo(
    () => normalizeNext(searchParams.get('next')),
    [searchParams]
  );
  const waitlistLogin = useMemo(
    () => parseWaitlistLoginParams(searchParams),
    [searchParams]
  );

  const [email, setEmail] = useState(waitlistLogin.email);
  const [inviteCode, setInviteCode] = useState(() =>
    initialInviteCodeFromSearchParams(searchParams)
  );
  const [token, setToken] = useState('');
  const [bizId, setBizId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const motionTokens = useLoginStepMotionTokens();
  const autoVerifyAttemptRef = useRef<string | null>(null);
  const autoLoginAttemptRef = useRef<string | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const effectiveInviteCode = inviteCode.trim();
  const canRequestCode = isValidLoginEmail(email) && !isLoading;
  const isMacOSOnboarding = isMacOSOnboardingRuntime(searchParams);

  useWebsiteTitlebar({
    sourceRef: pageRef,
    light: '#fafafa',
    dark: '#404040',
  });
  useMacOSOnboardingWindowRuntime({
    enabled: isMacOSOnboarding,
    stage: 'login',
  });

  const verifyLoginCode = useCallback(
    async (code: string) => {
      setError(null);
      setIsLoading(true);

      try {
        const user = await verifyWebsiteLoginToken(bizId, code, {
          inviteCode: effectiveInviteCode,
        });
        let state = await getOnboardingState();
        if (
          effectiveInviteCode &&
          state.onboarding_status === 'invite_required'
        ) {
          state = await applyOnboardingInviteCode(effectiveInviteCode);
        }
        writeOnboardingCompleteCache(user.id, state.onboarding_complete);

        if (isMacOSOnboarding) {
          syncMacOSOnboardingSessionSoon();
          navigate(
            resolveOnboardingPathForState(state, next, { runtime: 'native' }),
            { replace: true }
          );
          return;
        }

        if (state.onboarding_status === 'completed') {
          navigate(next, { replace: true });
          return;
        }
        if (state.onboarding_status === 'invite_required') {
          navigate(resolveOnboardingPathForState(state, next), {
            replace: true,
          });
          return;
        }
        navigate(resolveWebOnboardingDownloadPath(next), { replace: true });
      } catch (nextError) {
        setError(
          nextError instanceof Error
            ? nextError.message
            : 'Failed to verify login'
        );
      } finally {
        setIsLoading(false);
      }
    },
    [bizId, effectiveInviteCode, isMacOSOnboarding, navigate, next]
  );

  const requestLoginCode = useCallback(
    async (rawEmail: string) => {
      const nextEmail = rawEmail.trim();
      setEmail(nextEmail);
      setError(null);
      setIsLoading(true);

      try {
        const response = await sendWebsiteLoginEmail({
          email: nextEmail,
          inviteCode: effectiveInviteCode || undefined,
        });
        setBizId(response.bizId);
        setSuccess(true);
      } catch (nextError) {
        setError(
          nextError instanceof Error
            ? nextError.message
            : 'Failed to send login email'
        );
      } finally {
        setIsLoading(false);
      }
    },
    [effectiveInviteCode]
  );

  useEffect(() => {
    if (!waitlistLogin.email || success) {
      return;
    }

    setEmail(currentEmail =>
      currentEmail.trim() ? currentEmail : waitlistLogin.email
    );
  }, [success, waitlistLogin.email]);

  useEffect(() => {
    if (
      !waitlistLogin.autoLogin ||
      !isValidLoginEmail(waitlistLogin.email) ||
      success ||
      isLoading
    ) {
      return;
    }

    const autoLoginKey = waitlistLogin.email.toLowerCase();
    if (autoLoginAttemptRef.current === autoLoginKey) {
      return;
    }

    autoLoginAttemptRef.current = autoLoginKey;
    navigate(buildConsumedAutoLoginPath(searchParams), { replace: true });
    void requestLoginCode(waitlistLogin.email);
  }, [
    isLoading,
    navigate,
    requestLoginCode,
    searchParams,
    success,
    waitlistLogin.autoLogin,
    waitlistLogin.email,
  ]);

  useEffect(() => {
    if (!success) {
      autoVerifyAttemptRef.current = null;
      return;
    }
    if (isLoading) {
      return;
    }
    const code = token.trim();
    if (code.length !== 6) {
      autoVerifyAttemptRef.current = null;
      return;
    }
    if (autoVerifyAttemptRef.current === code) {
      return;
    }
    autoVerifyAttemptRef.current = code;
    void verifyLoginCode(code);
  }, [success, isLoading, token, verifyLoginCode]);

  const handleRequestCode = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!isValidLoginEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    await requestLoginCode(email);
  };

  const handleVerifyCode = (event: React.FormEvent) => {
    event.preventDefault();
    const code = token.trim();
    if (code.length < 6) {
      return;
    }
    autoVerifyAttemptRef.current = code;
    void verifyLoginCode(code);
  };

  const blurFilter =
    motionTokens.blurPx !== '' ? `blur(${motionTokens.blurPx})` : 'blur(0px)';
  const duration =
    motionTokens.durationSec > 0 ? motionTokens.durationSec : 0.001;
  const exitDuration =
    motionTokens.exitDurationSec > 0
      ? motionTokens.exitDurationSec
      : duration * 0.5;
  const shift = motionTokens.shiftYpx;
  const stepScale = motionTokens.scale;
  const easeCurve = motionTokens.ease.some(n => n !== 0)
    ? motionTokens.ease
    : 'easeOut';

  return (
    <div
      ref={pageRef}
      data-native-window-drag={isMacOSOnboarding ? 'true' : undefined}
      className="flex h-[100dvh] flex-col items-center justify-center overflow-hidden bg-(--login-page-background) px-4 py-6 text-foreground sm:py-10"
    >
      <div className="flex w-full max-w-[20rem] flex-col items-center gap-5">
        <div className="flex justify-center">
          <AppLogo className="size-16 text-black dark:text-white" />
        </div>

        <div className="relative flex h-[var(--login-step-panel-height)] w-full min-h-0 flex-col">
          <AnimatePresence mode="sync">
            <motion.div
              key={success ? 'verify' : 'email'}
              className="absolute inset-0 flex min-h-0 w-full origin-center flex-col items-center gap-5 overflow-y-auto"
              initial={{
                opacity: 0,
                scale: stepScale,
                filter: blurFilter,
                y: shift,
              }}
              animate={{
                opacity: 1,
                scale: 1,
                filter: 'blur(0px)',
                y: 0,
              }}
              exit={{
                opacity: 0,
                scale: stepScale,
                filter: blurFilter,
                y: shift,
                transition: {
                  duration: exitDuration,
                  ease: easeCurve,
                },
              }}
              transition={{
                duration,
                ease: easeCurve,
              }}
            >
              <h1 className="login-step-heading">
                {success ? 'Check your email' : 'Log in to Bridge'}
              </h1>

              {success ? (
                <div className="flex w-full flex-col items-center gap-1 px-1 text-center">
                  <p className="text-sm leading-snug text-muted-foreground">
                    We&apos;ve sent you a temporary login link.
                  </p>
                  <p className="text-sm leading-snug text-muted-foreground">
                    Please check your inbox at
                  </p>
                  <p className="break-all text-sm font-medium leading-snug text-foreground">
                    {email}
                  </p>
                </div>
              ) : null}

              {success ? (
                <form onSubmit={handleVerifyCode} className={loginFormClass}>
                  <div className="space-y-2">
                    <label
                      htmlFor="token"
                      className="sr-only text-sm font-medium text-foreground"
                    >
                      Verification code
                    </label>
                    <div className="flex flex-col gap-1">
                      <Input
                        id="token"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="6-digit code"
                        value={token}
                        onChange={event => setToken(event.target.value)}
                        disabled={isLoading}
                        maxLength={6}
                        className={cn(
                          inputClass,
                          'text-base text-center font-mono tracking-[0.35em] placeholder:font-mono placeholder:tracking-normal placeholder:text-[1em]'
                        )}
                        aria-label="Verification code"
                        aria-invalid={error ? true : undefined}
                        aria-describedby={error ? 'token-error' : undefined}
                      />
                      {error ? (
                        <p
                          id="token-error"
                          role="alert"
                          className={loginFieldErrorClass}
                        >
                          {error}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <Button
                      type="submit"
                      className={cn(primaryButtonClass)}
                      disabled={isLoading || token.trim().length < 6}
                    >
                      {isLoading ? 'Verifying…' : 'Continue'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className={cn(secondaryButtonClass)}
                      onClick={() => {
                        setSuccess(false);
                        setToken('');
                        setBizId('');
                        setError(null);
                      }}
                      disabled={isLoading}
                    >
                      Use a different email
                    </Button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleRequestCode} className={loginFormClass}>
                  <div className="space-y-2">
                    <label
                      htmlFor="email"
                      className="sr-only text-sm font-medium text-foreground"
                    >
                      Email
                    </label>
                    <div className="flex flex-col gap-1">
                      <Input
                        id="email"
                        type="email"
                        autoComplete="email"
                        placeholder="Enter your email address..."
                        value={email}
                        onChange={event => setEmail(event.target.value)}
                        disabled={isLoading}
                        required
                        className={inputClass}
                        aria-invalid={error ? true : undefined}
                        aria-describedby={error ? 'email-error' : undefined}
                      />
                      {error ? (
                        <p
                          id="email-error"
                          role="alert"
                          className={loginFieldErrorClass}
                        >
                          {error}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="inviteCode"
                      className="sr-only text-sm font-medium text-foreground"
                    >
                      Invite code
                    </label>
                    <Input
                      id="inviteCode"
                      type="text"
                      autoComplete="off"
                      placeholder="Invite code (optional)"
                      value={inviteCode}
                      onChange={event => setInviteCode(event.target.value)}
                      disabled={isLoading}
                      className={inputClass}
                      aria-label="Invite code"
                    />
                  </div>

                  <Button
                    type="submit"
                    className={cn(primaryButtonClass)}
                    disabled={!canRequestCode}
                  >
                    {isLoading ? 'Sending…' : 'Continue with email'}
                  </Button>

                  <p className="text-center text-(length:--login-caption-font-size) leading-relaxed text-muted-foreground">
                    New here? Open{' '}
                    <a
                      href="https://bridge.surf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-black underline-offset-2 hover:underline dark:text-white"
                    >
                      Bridge
                    </a>{' '}
                    to create an account.
                  </p>
                </form>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
